import {
  CandleFetchParams,
  CandlePoint,
  ExchangeCandleRestAdapter,
} from "../../candle.types";
import { normalizeCandleSymbol } from "../../candle-symbols";
import { toBybitInterval } from "../../candle-intervals";
import { dedupeCandles } from "./rest-adapter.utils";

interface BybitKlineResponse {
  retCode: number;
  retMsg: string;
  result?: {
    list?: [string, string, string, string, string, string, string][];
  };
}

const BYBIT_URL = "https://api.bybit.com/v5/market/kline";
const DEFAULT_LIMIT = 500;
const MAX_LIMIT = 1000;

export const bybitCandlesRestAdapter: ExchangeCandleRestAdapter = {
  async fetchCandles(params: CandleFetchParams): Promise<CandlePoint[]> {
    const symbol = normalizeCandleSymbol("bybit", params.symbol);
    const query = new URLSearchParams({
      category: "linear",
      symbol,
      interval: toBybitInterval(params.interval),
      limit: String(Math.min(params.limit ?? DEFAULT_LIMIT, MAX_LIMIT)),
    });

    if (params.startTime) query.set("start", String(params.startTime));
    if (params.endTime) query.set("end", String(params.endTime));

    const response = await fetch(`${BYBIT_URL}?${query.toString()}`);
    if (!response.ok) {
      throw new Error("Price chart is temporarily unavailable");
    }

    const payload = (await response.json()) as BybitKlineResponse;
    if (payload.retCode !== 0) {
      throw new Error(
        payload.retMsg && !/request failed/i.test(payload.retMsg)
          ? `bybit: ${payload.retMsg}`
          : "Price chart is temporarily unavailable"
      );
    }

    const rows = payload.result?.list ?? [];
    const candles: CandlePoint[] = rows.map((row) => ({
      startTime: Number(row[0]),
      openPrice: row[1],
      highPrice: row[2],
      lowPrice: row[3],
      closePrice: row[4],
      volume: row[5],
    }));

    return dedupeCandles(candles);
  },
};

